import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Typography
} from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  type Application,
  applicationsAccessCreateMutation,
  applicationsApplicationsAccessListOptions,
  applicationsApplicationsAccessListQueryKey,
  applicationsApplicationsListOptions,
  organizationsAccessRetrieveQueryKey,
  organizationsAccessRolesCreateMutation
} from 'src/api/main'
import { ContentSpinner } from 'src/components'
import {
  appRoleIdSet,
  clampToSingleRole,
  rolesOwnedByApp
} from 'src/features/apps/utils/appAccess'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { workspaceUsersListQueryKey } from '../hooks'
import { mergeManagedRoleIds } from '../utils'

import { UserAppAccessRow, activeKey, roleKey } from './UserAppAccessRow'

const { Text } = Typography

type Props = {
  workspaceId: number
  userId: number
  organizationAccessId: number
  /** Roles currently on the access record (`record.roles`). */
  currentRoles: { id: number }[]
  readOnly?: boolean
}

// `${appId}_active` → boolean, `${appId}_role` → OrganizationRole id[].
type FormShape = Record<string, boolean | number[] | undefined>

export const UserDetailAccessTab = ({
  workspaceId,
  userId,
  organizationAccessId,
  currentRoles,
  readOnly = false
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm<FormShape>()
  const [isDirty, setIsDirty] = useState(false)
  // Ref mirrors `isDirty` so the hydrate effect can read it without becoming
  // a dependency — otherwise toggling dirty would re-run server-data hydration.
  const isDirtyRef = useRef(false)
  const snapshotRef = useRef<FormShape>({})

  useEffect(() => {
    isDirtyRef.current = isDirty
  }, [isDirty])

  const { data: appsPage, isLoading: isLoadingApps } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: workspaceId > 0
  })

  const { data: accessPage, isLoading: isLoadingAccess } = useQuery({
    ...applicationsApplicationsAccessListOptions({
      query: { organization: workspaceId, user: userId, per_page: 200 }
    }),
    enabled: workspaceId > 0 && userId > 0
  })

  const apps = useMemo<Application[]>(
    () => appsPage?.results ?? [],
    [appsPage?.results]
  )

  useEffect(() => {
    const results = accessPage?.results
    if (!results) return
    const next: FormShape = {}
    const rolesByAppId = new Map(
      apps.map((app) => [app.id, appRoleIdSet(app.related_roles)])
    )
    results.forEach((access) => {
      const appId = access.application.id
      next[activeKey(appId)] = true
      // `access.roles` spans every app, so narrow it to this app's own roles
      // before seeding — otherwise each row shows the whole workspace's roles.
      next[roleKey(appId)] = clampToSingleRole(
        rolesOwnedByApp(access.roles, rolesByAppId.get(appId) ?? new Set()).map(
          (r) => r.id
        )
      )
    })
    snapshotRef.current = next
    // Don't clobber in-flight edits with a background refetch.
    if (!isDirtyRef.current) {
      form.setFieldsValue(next)
    }
  }, [accessPage?.results, apps, form])

  const { mutateAsync: setRoles, isPending: isSavingRoles } = useMutation({
    ...organizationsAccessRolesCreateMutation()
  })

  const { mutate: saveAccess, isPending: isSavingAccess } = useMutation({
    ...applicationsAccessCreateMutation(),
    onSuccess: async () => {
      // Role IDs live on OrganizationAccess via a separate endpoint —
      // matches WEB's convention; a single Save commits both atomically.
      const values = form.getFieldsValue()
      const roleIds = apps
        .filter((app) => values[activeKey(app.id)])
        .flatMap((app) =>
          clampToSingleRole(
            (values[roleKey(app.id)] as number[] | undefined) ?? []
          )
        )

      // This tab manages app-scoped roles only (those selectable per app).
      const managed = new Set(
        apps.flatMap((app) => app.related_roles?.map((r) => r.id) ?? [])
      )
      const nextRoles = mergeManagedRoleIds(
        currentRoles.map((r) => r.id),
        managed,
        roleIds
      )

      try {
        await setRoles({
          path: { id: organizationAccessId },
          body: { roles: nextRoles }
        })
        await invalidateQueries([
          applicationsApplicationsAccessListQueryKey({
            query: { organization: workspaceId, user: userId }
          }),
          organizationsAccessRetrieveQueryKey({
            path: { id: organizationAccessId }
          }),
          workspaceUsersListQueryKey(workspaceId)
        ])
        setIsDirty(false)
        message.success(t('users.detail.access.saveSuccess'))
      } catch (err) {
        message.error(t('users.detail.access.saveFailed'))
        showApiErrors(err)
      }
    },
    onError: (e) => {
      message.error(t('users.detail.access.saveFailed'))
      showApiErrors(e)
    }
  })

  const isSaving = isSavingAccess || isSavingRoles

  const handleSave = () => {
    const values = form.getFieldsValue()
    const applications = apps
      .filter((app) => values[activeKey(app.id)])
      .map((app) => {
        const ids = (values[roleKey(app.id)] as number[] | undefined) ?? []
        // App access carries a single optional "primary" role; the full set of
        // multi-role grants flows through organizationsAccessRoles (above). Use
        // the first selected role as the primary, else null.
        const roleName =
          ids.length > 0
            ? (app.related_roles?.find((r) => r.id === ids[0])?.name ?? null)
            : null

        return { id: app.id, role: roleName }
      })

    // `users` body field expects OrganizationAccess ids (not User ids) —
    // matches WEB's convention; passing User.id triggers a backend error.
    saveAccess({
      path: { organization_id: workspaceId },
      body: { users: [organizationAccessId], applications }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    form.setFieldsValue(snapshotRef.current)
    setIsDirty(false)
  }

  if (isLoadingApps || isLoadingAccess) {
    return (
      <Card>
        <ContentSpinner minHeight={240} />
      </Card>
    )
  }

  if (apps.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('users.detail.access.emptyAllApps')}
            </Text>
          }
        />
      </Card>
    )
  }

  return (
    <Card size="small" styles={{ body: { padding: 0 } }}>
      <Form
        form={form}
        component={false}
        onValuesChange={() => {
          if (!readOnly) setIsDirty(true)
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--ant-color-border-secondary)'
          }}
        >
          <Flex vertical style={{ lineHeight: 1.25 }}>
            <Text strong style={{ fontSize: 13 }}>
              {t('users.detail.access.tabTitle')}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('users.detail.access.tabSubtitle')}
            </Text>
          </Flex>
          {!readOnly ? (
            <Flex gap={8}>
              <Button onClick={handleCancel} disabled={isSaving || !isDirty}>
                {t('users.editForm.cancel')}
              </Button>
              <Button
                type="primary"
                loading={isSaving}
                disabled={!isDirty}
                onClick={handleSave}
              >
                {t('users.editForm.save')}
              </Button>
            </Flex>
          ) : null}
        </Flex>

        <Flex vertical gap={8} style={{ padding: 10 }}>
          {apps.map((app) => (
            <UserAppAccessRow
              key={app.id}
              app={app}
              form={form}
              readOnly={readOnly}
            />
          ))}
        </Flex>
      </Form>
    </Card>
  )
}
